import { useState, useEffect } from 'react';
import axios from 'axios';
import { DataGrid } from '@mui/x-data-grid';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { Link } from 'react-router-dom';
import Actions from './Actions';
import MetaData from '../Layouts/MetaData';
import BackdropLoader from '../Layouts/BackdropLoader';

const FooterColumnTable = () => {
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(true);
    const [columns, setColumns] = useState([]);

    const { user } = useSelector((state) => state.user);
    const isSuperAdmin = user.role === "admin";
    const perms = user.adminRole?.permissions?.footer || {};
    const canCreate = isSuperAdmin || perms.create;
    const canEdit = isSuperAdmin || perms.edit;
    const canDelete = isSuperAdmin || perms.delete;

    const fetchColumns = async () => {
        try {
            const { data } = await axios.get('/api/v1/admin/footer-columns');
            setColumns(data.columns);
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to load footer columns", { variant: "error" });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchColumns();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const deleteHandler = async (id) => {
        try {
            await axios.delete(`/api/v1/admin/footer-column/${id}`);
            enqueueSnackbar("Footer column deleted", { variant: "success" });
            setColumns((prev) => prev.filter((c) => c._id !== id));
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to delete column", { variant: "error" });
        }
    };

    const gridColumns = [
        {
            field: "heading",
            headerName: "Heading",
            minWidth: 160,
            flex: 0.6,
        },
        {
            field: "side",
            headerName: "Side",
            minWidth: 90,
            flex: 0.3,
        },
        {
            field: "blockSummary",
            headerName: "Blocks",
            minWidth: 220,
            flex: 1,
        },
        {
            field: "order",
            headerName: "Order",
            type: "number",
            minWidth: 80,
            flex: 0.3,
        },
        {
            field: "active",
            headerName: "Active",
            minWidth: 90,
            flex: 0.3,
            renderCell: (params) => (
                params.row.active
                    ? <span className="text-green-700 bg-green-100 px-2 py-0.5 rounded-full text-xs font-medium">Active</span>
                    : <span className="text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full text-xs font-medium">Hidden</span>
            ),
        },
        {
            field: "actions",
            headerName: "Actions",
            minWidth: 100,
            flex: 0.3,
            sortable: false,
            renderCell: (params) => (
                <Actions editRoute={"footer-column"} deleteHandler={deleteHandler} id={params.row.id} name={params.row.heading} canEdit={canEdit} canDelete={canDelete} />
            ),
        },
    ];

    const rows = columns.map((col) => ({
        id: col._id,
        heading: col.heading,
        side: col.side,
        blockSummary: (col.blocks || []).map((b) => b.type).join(", ") || "empty",
        order: col.order,
        active: col.active,
    }));

    return (
        <>
            <MetaData title="Admin: Footer Columns | 1313 Mart" />
            {loading && <BackdropLoader />}

            <div className="flex justify-between items-center mb-4">
                <div>
                    <Link to="/admin/footer" className="text-sm text-primary-blue hover:underline">&larr; Back to Footer</Link>
                    <h1 className="text-lg font-medium uppercase mt-1">Footer Columns</h1>
                </div>
                {canCreate && (
                    <Link to="/admin/footer-column/new" className="py-2 px-4 rounded shadow font-medium text-white bg-primary-blue hover:shadow-lg">New Column</Link>
                )}
            </div>

            <div className="bg-white rounded-xl shadow-lg w-full" style={{ height: 470 }}>
                <DataGrid
                    rows={rows}
                    columns={gridColumns}
                    pageSize={10}
                    disableSelectIconOnClick
                    sx={{ boxShadow: 0, border: 0 }}
                />
            </div>
        </>
    );
};

export default FooterColumnTable;
